/**
 * The sign-in challenge: shared vocabulary for the OTP step (IN-332).
 *
 * Pure, and imported by both the route handler and the two client
 * components, so the copy a person reads and the copy a test asserts on are
 * the same string. Nothing here reaches the network or a cookie.
 */

/** Six boxes on screen, six digits in `otp_service`. */
export const CODE_LENGTH = 6;

/** `?flow=` on /auth/verify. Sign-up confirms an address; sign-in clears a second factor. */
export const SIGNIN_FLOW = 'signin';
export const SIGNUP_FLOW = 'signup';

/**
 * What a rejected code says — one fixed sentence, whatever the API's own
 * wording was.
 *
 * The upstream detail is helpful ("Invalid or expired OTP. 2 attempts
 * remaining.") but it is assembled from several branches, and one of them —
 * `login-otp`'s "No account found with this email or phone number. Please
 * sign up first." — answers a question a signed-out stranger must not be
 * able to ask. Passing upstream text straight through means the day a new
 * branch is added, this screen starts repeating it. So it does not.
 */
export const INVALID_CODE_MESSAGE =
  'That code is not valid, or it has expired. Check the latest one you were sent, or send a new code.';

/** The pending cookies outlive nothing; say so plainly rather than 500ing. */
export const CHALLENGE_EXPIRED_MESSAGE =
  'This sign-in attempt has expired. Enter your email and password again.';

/**
 * Turn `Retry-After` seconds into something readable.
 *
 * The API owns the limit and the number; this only words it. Rounding up
 * matters — telling someone "in 4 minutes" when 4m30s remain earns a second
 * refusal and the impression the screen is lying.
 */
export function formatRetryAfter(seconds) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return 'in a few minutes';
  if (value < 60) return `in ${Math.ceil(value)} seconds`;
  const minutes = Math.ceil(value / 60);
  return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * Where the code went, in the API's own words.
 *
 * `login-otp` reports `delivery_method` because it is the only layer that
 * knows: a number the SMS provider rejects is retried over WhatsApp, so
 * inferring "SMS" from "we sent a phone number" has been wrong before. Email
 * has no `delivery_method` — there is only one email channel — so it is the
 * fallback rather than a guess.
 */
export function deliveryChannel(challenge, identifier) {
  const reported = challenge?.delivery_method;
  if (typeof reported === 'string' && reported.trim()) return reported.trim();
  return String(identifier || '').includes('@') ? 'email' : 'SMS';
}

/** "We sent a 6-digit code to you by email" — the one place that sentence is built. */
export function sentCodeMessage(channel, identifier) {
  const where = identifier ? ` to ${identifier}` : '';
  return channel === 'email'
    ? `We sent a ${CODE_LENGTH}-digit code${where}`
    : `We sent a ${CODE_LENGTH}-digit code by ${channel}${where}`;
}
